import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

type HoveredEdge = { id: string; source: string; target: string };

type HoverStoreType = {
  /** Id of the entity currently hovered on either surface (list or canvas). */
  hoveredId: string | null;
  /** Id of the entity whose context menu is open; stays highlighted like a hover. */
  pinnedId: string | null;
  /** Id of the relationship currently hovered on the canvas. */
  hoveredEdgeId: string | null;
  /** Its two endpoint ids, so an entity can answer "am I an endpoint?" without a lookup. */
  hoveredEdgeEndpoints: readonly [string, string] | null;
  setHovered: (id: string | null) => void;
  setPinned: (id: string | null) => void;
  setHoveredEdge: (edge: HoveredEdge | null) => void;
  unpin: (id: string) => void;
  clear: () => void;
};

const useHover = create<HoverStoreType>((set, get) => ({
  hoveredId: null,
  pinnedId: null,
  hoveredEdgeId: null,
  hoveredEdgeEndpoints: null,

  setHovered: (id: string | null) => {
    if (get().hoveredId === id) return;
    set({ hoveredId: id });
  },

  setPinned: (id: string | null) => {
    if (get().pinnedId === id) return;
    set({ pinnedId: id });
  },

  setHoveredEdge: (edge: HoveredEdge | null) => {
    const id = edge?.id ?? null;
    if (get().hoveredEdgeId === id) return;

    set({
      hoveredEdgeId: id,
      hoveredEdgeEndpoints: edge ? [edge.source, edge.target] : null,
    });
  },

  unpin: (id: string) => {
    if (get().pinnedId !== id) return;
    set({ pinnedId: null });
  },

  clear: () => {
    const { hoveredId, hoveredEdgeId } = get();
    if (hoveredId === null && hoveredEdgeId === null) return;

    set({ hoveredId: null, hoveredEdgeId: null, hoveredEdgeEndpoints: null });
  },
}));

export const useIsNodeHovered = (id: string) =>
  useHover((state) => state.hoveredId === id || state.pinnedId === id);

export const useIsEdgeHovered = (id: string) =>
  useHover((state) => state.hoveredEdgeId === id);

/** True for either endpoint of the hovered relationship (QSP-3494 AC6/AC7). */
export const useIsEndpointOfHoveredEdge = (id: string) =>
  useHover(
    (state) =>
      state.hoveredEdgeEndpoints?.[0] === id ||
      state.hoveredEdgeEndpoints?.[1] === id,
  );

export const useHoverActions = () =>
  useHover(
    useShallow((state) => ({
      setHovered: state.setHovered,
      setPinned: state.setPinned,
      setHoveredEdge: state.setHoveredEdge,
      unpin: state.unpin,
      clear: state.clear,
    })),
  );

export const { getState: getHoverState, setState: setHoverState } = useHover;

export default useHover;
